// ticket-dag-reachability.ts
//
// Transitive `depends_on` walks, shared by the DAG validator (test-first rule
// 3) and the scheduler's RED-window rule (phase 06j). Pure; unknown ids are
// skipped (the validator's unknown-dependency rule reports them).
import type { Ticket } from "./schemas/ticket-schema.js";

/** Every ticket `id` depends on, directly or transitively (excludes `id`). */
export function ancestorsOf(id: string, byId: Map<string, Ticket>): Set<string> {
  const seen = new Set<string>();
  const queue = [...(byId.get(id)?.depends_on ?? [])];
  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined || seen.has(current)) continue;
    seen.add(current);
    queue.push(...(byId.get(current)?.depends_on ?? []));
  }
  return seen;
}

/** `id` ⇒ tickets that list it in `depends_on` (direct dependents). */
export function dependentsIndex(tickets: readonly Ticket[]): Map<string, Ticket[]> {
  const index = new Map<string, Ticket[]>();
  for (const ticket of tickets) {
    for (const dep of ticket.depends_on) index.set(dep, [...(index.get(dep) ?? []), ticket]);
  }
  return index;
}

/** Every ticket that depends on `id`, directly or transitively (excludes `id`). */
export function descendantsOf(id: string, dependents: Map<string, Ticket[]>): Set<string> {
  const seen = new Set<string>();
  const queue = [...(dependents.get(id) ?? [])];
  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined || seen.has(current.id)) continue;
    seen.add(current.id);
    queue.push(...(dependents.get(current.id) ?? []));
  }
  return seen;
}
